/**
 * Print/PDF helpers for Deep Research HTML report iframes.
 * Print-only CSS and DOM tweaks are applied on beforeprint and restored on afterprint
 * so the on-screen report layout is unchanged.
 */

export const RESEARCH_REPORT_PRINT_STYLE = `
@media print {
  @page { size: landscape; margin: 8mm; }
  body { background: #fff !important; }
  .report { width: 100% !important; padding: 0 !important; }
  .table-tools { display: none !important; }
  .table-wrap, .report, main, body { overflow: visible !important; }
  table {
    width: 100% !important;
    min-width: 0 !important;
    max-width: 100% !important;
    table-layout: fixed !important;
  }
  th, td {
    display: table-cell !important;
    visibility: visible !important;
    min-width: 0 !important;
    max-width: none !important;
    white-space: normal !important;
    word-break: break-word;
    overflow-wrap: anywhere;
    font-size: 8px !important;
    line-height: 1.25 !important;
    padding: 3px 4px !important;
  }
  th[hidden], td[hidden] {
    display: table-cell !important;
    visibility: visible !important;
  }
  th {
    white-space: normal !important;
    text-transform: none !important;
    letter-spacing: 0 !important;
    position: static !important;
  }
  td:first-child, th:first-child {
    width: 28px !important;
    min-width: 28px !important;
  }
  details.collapsible > summary,
  .collapsible-summary {
    display: none !important;
  }
  details.collapsible .collapsible-body,
  details.collapsible:not([open]) .collapsible-body {
    display: block !important;
  }
}
`;

type PrintPrepState = {
  checkboxes: Array<{ element: HTMLInputElement; checked: boolean }>;
  hiddenCells: HTMLElement[];
  details: Array<{ element: HTMLDetailsElement; open: boolean }>;
};

let activePrintState: PrintPrepState | null = null;

function ensurePrintStyle(doc: Document): void {
  if (doc.getElementById("theo-research-report-print-style")) return;
  const style = doc.createElement("style");
  style.id = "theo-research-report-print-style";
  style.textContent = RESEARCH_REPORT_PRINT_STYLE;
  doc.head.appendChild(style);
}

function applyPrintPrep(doc: Document): void {
  const hiddenCells = Array.from(doc.querySelectorAll<HTMLElement>("th[hidden], td[hidden]"));
  const checkboxes = Array.from(doc.querySelectorAll<HTMLInputElement>("[data-table-column-checkbox]")).map(
    (element) => ({
      element,
      checked: element.checked,
    })
  );
  const details = Array.from(doc.querySelectorAll<HTMLDetailsElement>("details.collapsible")).map((element) => ({
    element,
    open: element.open,
  }));

  activePrintState = { checkboxes, hiddenCells, details };

  checkboxes.forEach(({ element }) => {
    element.checked = true;
  });
  hiddenCells.forEach((cell) => {
    cell.hidden = false;
  });
  details.forEach(({ element }) => {
    element.open = true;
  });

  doc.querySelectorAll("[data-table-card]").forEach((card) => {
    card.querySelectorAll<HTMLInputElement>("[data-table-column-checkbox]").forEach((checkbox) => {
      checkbox.dispatchEvent(new Event("change", { bubbles: true }));
    });
  });
}

function restoreAfterPrint(doc: Document): void {
  const saved = activePrintState;
  activePrintState = null;
  if (!saved) return;

  saved.checkboxes.forEach(({ element, checked }) => {
    element.checked = checked;
  });
  saved.hiddenCells.forEach((cell) => {
    cell.hidden = true;
  });
  saved.details.forEach(({ element, open }) => {
    element.open = open;
  });

  doc.querySelectorAll("[data-table-card]").forEach((card) => {
    card.querySelectorAll<HTMLInputElement>("[data-table-column-checkbox]").forEach((checkbox) => {
      checkbox.dispatchEvent(new Event("change", { bubbles: true }));
    });
  });
}

/**
 * Attach print-only handlers to a report iframe. Returns cleanup for React effects.
 */
export function bindResearchReportPrintHandlers(
  doc: Document | null | undefined,
  win: Window | null | undefined
): () => void {
  if (!doc || !win) return () => {};

  ensurePrintStyle(doc);

  const onBeforePrint = () => applyPrintPrep(doc);
  const onAfterPrint = () => restoreAfterPrint(doc);

  win.addEventListener("beforeprint", onBeforePrint);
  win.addEventListener("afterprint", onAfterPrint);

  return () => {
    win.removeEventListener("beforeprint", onBeforePrint);
    win.removeEventListener("afterprint", onAfterPrint);
    activePrintState = null;
  };
}
